/**
 * useLocations hook — CRUD operations for user geographic locations.
 *
 * Follows the same pattern as use-user-settings.ts / use-medications.ts:
 * - Uses @tanstack/react-query for data fetching
 * - Immediate mutations (not deferred to a Save button)
 * - Optimistic cache invalidation on create/update/delete
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createApiClient,
  type CreateLocationRequest,
  type UpdateLocationRequest,
  type LocationResponse,
  type ListLocationsResponse,
} from "@floline/api-client";
import { API_URL } from "@/env";

export const locationKeys = {
  all: ["locations"] as const,
};

const apiClient = createApiClient(
  API_URL
);

async function fetchLocations(): Promise<LocationResponse[]> {
  const response: ListLocationsResponse = await apiClient.listLocations();
  return response.data;
}

export interface UseLocationsOptions {
  enabled?: boolean;
}

export function useLocations(options: UseLocationsOptions = {}) {
  const { enabled = true } = options;
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: locationKeys.all,
    queryFn: fetchLocations,
    staleTime: 5 * 60 * 1000,
    enabled,
  });

  const createMutation = useMutation({
    mutationFn: async (data: CreateLocationRequest) => {
      const response = await apiClient.createLocation(data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: locationKeys.all });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: UpdateLocationRequest;
    }) => {
      const response = await apiClient.updateLocation(id, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: locationKeys.all });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.deleteLocation(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: locationKeys.all });
    },
  });

  return {
    /** List of user locations */
    locations: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,

    /** Create a new location by geocoding a city query */
    createLocation: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    createError: createMutation.error,

    /** Update a location (rename / set as default) */
    updateLocation: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    updateError: updateMutation.error,

    /** Delete a location (soft-delete) */
    deleteLocation: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
    deleteError: deleteMutation.error,
  };
}
